import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {TriggerExpression} from '@models/adminstration/machine/trigger/trigger-expression';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {TriggerTimeCondition} from '@models/adminstration/machine/trigger/trigger-time-condition';
import {TriggerConfigurationService} from '@services/administration/machine/trigger/trigger-configuration.service';
import {TriggerExpressionTypes} from '@constants/administration/machine/trigger-expression-type';
import {DAYS_OF_WEEK} from '@constants/week-days-names';
import {TIME_INTERVAL_TYPES} from '@constants/time-interval.constant';
import {DateTimeService} from '@services/date-time/date-time.service';

@Component({
    selector: 'scitis-admin-trigger-time-expression-table',
    templateUrl: './trigger-time-expression-table.component.html',
    styleUrls: ['./trigger-time-expression-table.component.scss'],
    standalone: false
})
export class TriggerTimeExpressionTableComponent implements OnInit {

  @Input()
  isAdminOrHigher = false;

  @Input()
  triggerTimeExpressions: TriggerExpression[] = [];

  @Output()
  updatedTriggerTimeExpressions: EventEmitter<TriggerExpression[]> = new EventEmitter<TriggerExpression[]>();

  @Output()
  deletedTriggerTimeExpression: EventEmitter<TriggerExpression> = new EventEmitter<TriggerExpression>();

  form: UntypedFormGroup = new UntypedFormGroup({});
  timeIntervalTypes = TIME_INTERVAL_TYPES;
  time = {hour: 'HH', minute: 'MM'};
  selectedTimeIntervalType: string = '';
  atLeastOneValue: boolean = false;
  daysOfWeek = Object.values(DAYS_OF_WEEK);
  translatedDaysOfWeek: any[] = new Array<any>();

  constructor(private translate: TranslateService,
              private triggerConfigurationService: TriggerConfigurationService,
              private dateTimeService: DateTimeService) {
  }

  get formControls() {
    return this.form.controls;
  }

  ngOnInit(): void {
    this.generateForm();
    this.setTranslatedTypes();
  }

  addTriggerTimeExpressionDisabled(): boolean {
    return !this.isAdminOrHigher || !this.form.valid || !this.atLeastOneValue;
  }

  onTimeExpressionIntervalChange(selectedInterval: string): void {
    this.selectedTimeIntervalType = selectedInterval;
    this.atLeastOneInputFieldHasValue();
  }

  onFocusOut() {
    this.setFormValidation();
    this.atLeastOneInputFieldHasValue();
  }

  onAddTimeExpression() {
    const date = this.getDateTimeFromObject(this.formControls.time.value);
    if (this.form.valid) {
      const newTriggerExpression = new TriggerExpression();
      newTriggerExpression.triggerTimeCondition = new TriggerTimeCondition();
      newTriggerExpression.triggerTimeCondition.type = this.formControls.timeInterval.value;
      newTriggerExpression.triggerTimeCondition.time = date;
      this.triggerConfigurationService.deleteParametersOnAddTimeExpression(newTriggerExpression, this.formControls.dayOfWeek.value, this.formControls.dayOfMonth.value);
      newTriggerExpression.triggerExpressionType = TriggerExpressionTypes.TIME_EVAL;
      this.triggerTimeExpressions.push(newTriggerExpression);
      this.updatedTriggerTimeExpressions.emit(this.triggerTimeExpressions);
      this.resetFormFields();
    }
  }

  onRemoveTimeExpression(index: number, expression: TriggerExpression): void {
    this.triggerTimeExpressions.splice(index, 1);
    this.updatedTriggerTimeExpressions.emit(this.triggerTimeExpressions);
    this.deletedTriggerTimeExpression.emit(expression);
  }

  getDayOfMonthValue(timeCondition: TriggerTimeCondition | undefined) {
    return this.triggerConfigurationService.getDayOfMonthValue(timeCondition);
  }

  getDayOfWeekValue(timeCondition: TriggerTimeCondition | undefined) {
    return this.triggerConfigurationService.getDayOfWeekValue(timeCondition, this.translatedDaysOfWeek);
  }

  private generateForm(): void {
    this.form = new UntypedFormGroup({
      correlation: new UntypedFormControl(''),
      expressionType: new UntypedFormControl(''),
      signal: new UntypedFormControl(''),
      operator: new UntypedFormControl(null),
      lastValue: new UntypedFormControl(null),
      timeInterval: new UntypedFormControl(null),
      timeCorrelation: new UntypedFormControl(''),
      time: new UntypedFormControl('', [Validators.required, this.dateTimeService.timePickerValidator(this.time)]),
      dayOfWeek: new UntypedFormControl(''),
      dayOfMonth: new UntypedFormControl('')
    });

    this.setFormValidation();
  }

  private resetFormFields(): void {
    this.form.reset();
    this.formControls.time.setValue('');
    this.formControls.timeInterval.setValue('');
    this.formControls.dayOfWeek.setValue('');
    this.formControls.dayOfMonth.setValue('');

    this.setFormValidation();
  }

  private setFormValidation(): void {
    if (this.atLeastOneInputFieldHasValue()) {
      if (this.selectedTimeIntervalType === 'MONTHLY') {
        this.form.get('dayOfMonth')?.setValidators([Validators.min(1), Validators.max(31)]);
      } else {
        this.form.get('dayOfMonth')?.setValidators(null);
      }
    } else {
      this.form.get('dayOfMonth')?.setValidators(null);
    }
    this.form.get('dayOfMonth')?.updateValueAndValidity();
  }

  private atLeastOneInputFieldHasValue() {
    const timeValue = this.formControls.time.value;
    if (this.selectedTimeIntervalType === 'MONTHLY') {
      this.atLeastOneValue = timeValue.hour !== this.time.hour && timeValue.minute !== this.time.minute && this.formControls.timeInterval.value && this.formControls.dayOfMonth.value;
    } else if (this.selectedTimeIntervalType === 'WEEKLY') {
      this.atLeastOneValue = timeValue && timeValue.hour !== this.time.hour && timeValue.minute !== this.time.minute && this.formControls.timeInterval.value && this.formControls.dayOfWeek.value >= 0;
    } else {
      this.atLeastOneValue = timeValue && timeValue.hour !== this.time.hour && timeValue.minute !== this.time.minute && this.formControls.timeInterval.value;
    }
    return this.atLeastOneValue;
  }

  private getDateTimeFromObject(time: any): string {
    const date = new Date(Date.UTC(2000, 0, 1));
    date.setHours(time.hour);
    date.setMinutes(time.minute);
    return date.toISOString();
  }

  private setTranslatedTypes() {
    let index = 0;
    for (const element of this.daysOfWeek) {
      this.translatedDaysOfWeek = [...this.translatedDaysOfWeek, {
        key: index,
        value: this.translate.instant(element)
      }];
      index++;
    }
  }

}
